// @ts-nocheck
/**
 * sliderUtils.ts — Pure helpers for Template V2 slider value snap / OOB (DEC-059).
 */

export type SliderValueType = 'integer' | 'float';
export type SliderOrientation = 'horizontal' | 'vertical';

export const SLIDER_DEFAULT_MIN = 0;
export const SLIDER_DEFAULT_MAX = 100;
export const SLIDER_OOB_COLOR = '#DC2626';

/** Coerce a resolved raw value to a finite number, or null. */
export function coerceSliderNumber(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw;
  }
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Normalize min/max so lo <= hi even if authors swap them. */
export function normalizeSliderRange(min: number, max: number): { lo: number; hi: number } {
  return min <= max ? { lo: min, hi: max } : { lo: max, hi: min };
}

export function isSliderOutOfBounds(value: number, min: number, max: number): boolean {
  const { lo, hi } = normalizeSliderRange(min, max);
  return value < lo || value > hi;
}

/**
 * Snap a candidate to [min,max] using valueType / step.
 * Drag commits always go through this — OOB stored values are never produced by drag.
 */
export function snapSliderValue(
  raw: number,
  min: number,
  max: number,
  valueType: SliderValueType,
  step?: number,
): number {
  const { lo, hi } = normalizeSliderRange(min, max);
  let v = Math.max(lo, Math.min(hi, raw));
  const effectiveStep =
    step != null && step > 0
      ? step
      : valueType === 'integer'
        ? 1
        : undefined;
  if (effectiveStep != null) {
    const steps = Math.round((v - lo) / effectiveStep);
    v = lo + steps * effectiveStep;
    v = Math.max(lo, Math.min(hi, v));
  }
  if (valueType === 'integer') {
    v = Math.round(v);
  }
  return v;
}

/**
 * Visual thumb fraction in [0,1]. OOB values pin to 0 or 1 without changing storage.
 */
export function sliderVisualFraction(value: number, min: number, max: number): number {
  const { lo, hi } = normalizeSliderRange(min, max);
  if (hi === lo) {
    return 0;
  }
  if (value <= lo) {
    return 0;
  }
  if (value >= hi) {
    return 1;
  }
  return (value - lo) / (hi - lo);
}

/** Map a pointer fraction [0,1] along the track to a snapped value. */
export function valueFromSliderFraction(
  fraction: number,
  min: number,
  max: number,
  valueType: SliderValueType,
  step?: number,
): number {
  const { lo, hi } = normalizeSliderRange(min, max);
  const clamped = Math.max(0, Math.min(1, fraction));
  const raw = lo + clamped * (hi - lo);
  return snapSliderValue(raw, lo, hi, valueType, step);
}
